/**
 * Каркас страницы: шапка, боковое меню, область содержимого.
 *
 * Высота фиксирована высотой окна, и страница целиком НЕ прокручивается.
 * Прокрутка живёт внутри блоков. Причина простая: на экране, где показывают
 * состояние бэкапов, сводка и предупреждения должны оставаться на виду, пока
 * человек листает таблицу. Если уезжает всё сразу, вместо картины получается
 * лента, и понять «всё ли в порядке» можно только доскроллив до конца.
 */
import { useSyncExternalStore, type ReactNode } from 'react';
import { useConnection } from '../transport/hooks.ts';
import { signOut } from '../transport/socket.ts';
import { getServerTheme, getTheme, setTheme, subscribeTheme } from '../theme.ts';
import { THEMES, THEME_LABELS, type Theme } from '../../shared/theme.ts';

function ConnectionIndicator(): React.JSX.Element {
  const c = useConnection();

  const text =
    c.status === 'ready' ? 'на связи'
    : c.status === 'connecting' ? 'подключаюсь…'
    : c.status === 'authenticating' ? 'проверяю ключ…'
    : c.status === 'offline'
      ? c.reconnectInMs
        ? `нет связи, повтор через ${Math.round(c.reconnectInMs / 1000)} с`
        : 'нет связи'
    : c.status === 'auth-failed' ? 'ключ отклонён'
    : 'ожидание';

  return (
    <span className={`conn conn--${c.status}`} title={c.message ?? undefined}>
      <span className="conn__dot" aria-hidden="true" />
      {text}
    </span>
  );
}

const THEME_GLYPH: Record<Theme, string> = { auto: '◐', light: '☀', dark: '☾' };

function ThemeSwitch(): React.JSX.Element {
  const theme = useSyncExternalStore(subscribeTheme, getTheme, getServerTheme);

  return (
    <div className="themeswitch" role="group" aria-label="Оформление">
      {THEMES.map((t) => (
        <button
          key={t}
          type="button"
          className={`themeswitch__item${theme === t ? ' themeswitch__item--active' : ''}`}
          onClick={() => setTheme(t)}
          title={`Оформление: ${THEME_LABELS[t]}`}
          aria-pressed={theme === t}
        >
          <span aria-hidden="true">{THEME_GLYPH[t]}</span>
          <span className="themeswitch__label">{THEME_LABELS[t]}</span>
        </button>
      ))}
    </div>
  );
}

export interface NavItem {
  id: string;
  label: string;
  glyph: string;
  /** Число рядом с пунктом: сколько там требует внимания. */
  badge?: number;
  /** Разделитель перед пунктом: следующая группа по смыслу. */
  group?: string;
}

export function Layout({
  children,
  nav,
  active,
  onNavigate,
}: {
  children: ReactNode;
  nav?: NavItem[];
  active?: string;
  onNavigate?: (id: string) => void;
}): React.JSX.Element {
  const c = useConnection();

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__brand">
          <span className="shell__mark" aria-hidden="true">⛁</span>
          <span className="shell__name">
            Backup<span className="shell__name-hub">HUB</span>
          </span>
        </div>
        <div className="shell__meta">
          {c.owner && <span className="shell__owner">{c.owner}</span>}
          <ConnectionIndicator />
          <ThemeSwitch />
          {c.status === 'ready' && (
            <button
              type="button"
              className="button button--ghost"
              onClick={signOut}
              title="Ключ останется в браузере"
            >
              Выход
            </button>
          )}
        </div>
      </header>
      <div className="shell__body">
        {nav && nav.length > 0 && (
          <nav className="nav" aria-label="Разделы">
            {nav.map((item) => (
              <span key={`g-${item.id}`} className="nav__wrap">
              {item.group && <span className="nav__group">{item.group}</span>}
              <button
                key={item.id}
                type="button"
                className={`nav__item${active === item.id ? ' nav__item--active' : ''}`}
                onClick={() => onNavigate?.(item.id)}
                aria-current={active === item.id ? 'page' : undefined}
              >
                <span className="nav__glyph" aria-hidden="true">{item.glyph}</span>
                <span className="nav__label">{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="nav__badge" title="Требует внимания">{item.badge}</span>
                )}
              </button>
              </span>
            ))}
          </nav>
        )}
        <main className="main">{children}</main>
      </div>
    </div>
  );
}
